import React, {useState, useEffect} from 'react';
import map from 'lodash/fp/map';
import filter from 'lodash/fp/filter';
import contains from 'lodash/fp/contains';
import compose from 'lodash/fp/compose';
import __ from 'lodash/fp/placeholder';
import get from 'lodash/fp/get';
import set from 'lodash/fp/set';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import Paper from '@material-ui/core/Paper';

import {
  addVehicleValue,
  filterNotStarted,
  getVehiclesValue,
  getVehicleValue,
  getId,
  findId
} from '../../libs/report';
import useDateInterval from '../../hooks/useDateInterval';
import {withData} from '../../containers/DataContainer';
import QueueTable from '../../components/Tables/Queue';
import useStyles from '../styles';

import QueueDetails from './Details';

const Queue = ({
  data,
  chooseVehicle,
  fetchDeliveriesByDate,
  updateDeliveryStatus,
  updateDeliveryVehicle,
  createChooseEvent,
  createPausedEvent,
  createUnavailableEvent,
}) => {

  const classes = useStyles();

  const [detailData, setDetailData] = useState({});
  const [chooseData, setChooseData] = useState([]);
  const [maxEnrolled, setMaxEnrolled] = useState(1);
  const [isBusy, setBusy] = useState(true);
  const [isDetailBusy, setDetailBusy] = useState(true);
  const [path, setPath] = useState('root');
  const [startDate, setStartDate, endDate, setEndDate] = useDateInterval();

  const today = moment().endOf('day');
  const maxDate = today.date() < 25
    ? today.subtract(1, 'months').date(25)
    : today.date(25);

  const onChangeMaxEnrolled = event => {
    setMaxEnrolled(event.target.value)
  };

  useEffect(() => {
    const fetchData = async () => {
      setBusy(true);
      await fetchDeliveriesByDate({
        startDate: '' + startDate.valueOf(),
        endDate: '' + Math.max(endDate.valueOf(), startDate.valueOf())
      })
      setBusy(false);
    };
    fetchData();
  }, [startDate, endDate, fetchDeliveriesByDate]);

  const onRowClick = async ({rowData}) => {
    setBusy(true);
    setDetailData(rowData);
    setPath('detail');
    setBusy(false);
  };

  const deliveries = get('deliveries')(data);
  const vehiclesData = getVehiclesValue(deliveries);
  const queueData = compose(
    filterNotStarted,
    addVehicleValue(vehiclesData),
  )(deliveries);

  const getPairs = async _id => {
    try {
      const data = await chooseVehicle({
        _id,
        startDate: '' + startDate.valueOf(),
        endDate: '' + endDate.valueOf(),
        maxEnrolled
      });
      return data;
    } catch (e) {
      if (e.response && e.response.status === 404) return []
      console.log(e);
    }
  };

  const onChoose = async () => {
    setDetailBusy(true);
    setPath('choose');

    if (isEmpty(queueData)) {
      setDetailBusy(false);
      setPath('root');
      return;
    };

    // returns pairs with type [vehicleId, deliveryId]
    const pairs = await compose(
      getPairs,
      map(getId),
      filter(
        compose(
          contains(__, ['pending', 'event']),
          get('status')
        )
      )
    )(queueData);

    const chooseData =
      map(
        el => ({
          ...findId(el[1])(queueData),
          vehicleId: el[0],
          vehicleValue: getVehicleValue(vehiclesData)(el[0]),
          status: 'waiting'
        })
      )(pairs);
    setChooseData(chooseData);
    setDetailBusy(false);
  };

  const getToBeEnrolledDeliveries = () => {
    setDetailBusy(true);
    setChooseData(
      compose(
        map(set('status', 'enrolled')),
        filter({'status': 'waiting'}),
      )(queueData)
    );
    setDetailBusy(false);
    setPath('start');
  };

  const _bulkUpdateDeliveryStatus = async () => {
    setBusy(true);
    await Promise.all(
      map(updateDeliveryStatus)(chooseData)
    );
    setBusy(false);
    setPath('root');
  };

  const _updateDeliveryStatus = async () => {
    setBusy(true);
    await updateDeliveryStatus({...detailData, status: 'enrolled'});
    setPath('root');
    setBusy(false);
  };

  const _updateDeliveryVehicle = async () => {
    setDetailBusy(true);
    await Promise.all([
      createChooseEvent(),
      createPausedEvent(),
      createUnavailableEvent({
        startDate: '' + startDate.valueOf(),
        endDate: '' + endDate.valueOf(),
        maxEnrolled
      }),
      ...map(updateDeliveryVehicle)(chooseData)
    ]);
    setDetailBusy(false);
    setPath('root');
  };

  return <>
    <Paper className={classes.paper}>
      <QueueTable
        data={queueData}
        {...{
          onRowClick,
          onChoose,
          maxEnrolled,
          onChangeMaxEnrolled,
          getToBeEnrolledDeliveries,
          isBusy,
          startDate,
          setStartDate,
          endDate,
          setEndDate,
          maxDate
        }}
      />
    </Paper>
    <QueueDetails
      updateDeliveryStatus={_updateDeliveryStatus}
      updateDeliveryVehicle={_updateDeliveryVehicle}
      bulkUpdateDeliveryStatus={_bulkUpdateDeliveryStatus}
      isBusy={isDetailBusy}
      {...{path, setPath, detailData, chooseData}}
    />
  </>;
};

export default withData(Queue);
